import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_WIDTH, GAME_HEIGHT } from "../config";
import { THEME, panel, button } from "../ui/theme";
import {
  addCastleBackdrop, addMenuBackdrop, addMenuHeader, addContentPanel, addQuitButton,
  addEmptyState, beginSceneLifecycle, menuButton, MENU_SAFE_MARGIN,
  type SceneLifecycle
} from "../ui/menuUi";
import { getUserId } from "../backend/auth";
import {
  fetchFriends, fetchPendingRequests, fetchBlocked,
  sendFriendRequest, sendFriendRequestByCode,
  searchPlayers, lookupByFriendCode,
  respondToRequest, cancelOutgoingRequest,
  removeFriend, blockUser, unblockUser,
  FriendSummary, PlayerSearchResult
} from "../backend/friends";
import { fetchMyProfile } from "../backend/profile";
import { isBackendConfigured } from "../backend/client";
import { showAlert, showConfirm } from "../ui/modal";
import { drawAvatar, formatNameWithTitle } from "../ui/avatar";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

async function reportFriends(
  scene: Phaser.Scene,
  res: { ok: true } | { ok: false; error: string }
): Promise<boolean> {
  if (res.ok) return true;
  await showAlert(scene, "Error", res.error);
  return false;
}

/* ── Types ───────────────────────────────────────────────────────────────── */

type Tab = "FRIENDS" | "PENDING" | "BLOCKED";

/* ── Layout constants ────────────────────────────────────────────────────── */

const CONTENT_X = 40;
const CONTENT_W = GAME_WIDTH - 80;
const HERO_Y = 104;
const HERO_H = 56;
const TAB_Y = 176;
const LIST_Y = 210;
const LIST_BOTTOM = GAME_HEIGHT - 62;
const CARD_H = 68;
const CARD_GAP = 6;
const PAGE_SIZE = Math.floor((LIST_BOTTOM - LIST_Y) / (CARD_H + CARD_GAP));

/* ── Scene ───────────────────────────────────────────────────────────────── */

export class FriendsScene extends Phaser.Scene {
  private lifecycle!: SceneLifecycle;
  private listGroup!: Phaser.GameObjects.Group;
  private activeTab: Tab = "FRIENDS";
  private page = 0;
  private requestVersion = 0;
  private tabContainers: Phaser.GameObjects.Container[] = [];
  private tabLabels: { tab: Tab; label: Phaser.GameObjects.Text }[] = [];
  private pendingInCount = 0;
  private friendCode = "";

  constructor() { super("FriendsScene"); }

  create(): void {
    this.lifecycle = beginSceneLifecycle(this);
    this.listGroup = this.add.group();
    this.activeTab = "FRIENDS";
    this.page = 0;
    this.tabContainers = [];
    this.tabLabels = [];

    // ── Chrome ──
    addMenuBackdrop(this, { darkness: 0.74 });
    addContentPanel(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, "iron", 0.96);
    addCastleBackdrop(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, { veil: "dark" });
    addMenuHeader(this, {
      title: "FRIENDS",
      onBack: () => goTo(this, "MenuScene"),
    });
    addQuitButton(this);
    this.lifecycle.key("keydown-ESC", () => goTo(this, "MenuScene"));

    if (!isBackendConfigured() || !getUserId()) {
      addEmptyState(this, GAME_WIDTH / 2, GAME_HEIGHT / 2,
        "Not Connected", "Backend not configured or not signed in.");
      return;
    }

    // ── Hero Card ──
    this.drawHeroCard();

    // ── Tab Bar ──
    this.drawTabBar();

    // ── Pre-load pending count for badge ──
    void this.preloadCounts();

    // ── Initial tab load ──
    void this.refreshTab();
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Hero Card: Friend Code + COPY + FIND PLAYERS                          */
  /* ──────────────────────────────────────────────────────────────────────── */

  private drawHeroCard(): void {
    panel(this, CONTENT_X, HERO_Y, CONTENT_W, HERO_H, "iron");

    this.add.text(CONTENT_X + 16, HERO_Y + 10, "YOUR FRIEND CODE", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.goldDim,
    });

    const codeLabel = this.add.text(CONTENT_X + 16, HERO_Y + 28, "loading…", {
      fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.gold,
    });

    // COPY button right of code
    const copyBtn = this.add.text(CONTENT_X + 260, HERO_Y + 32, "⧉ COPY", {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.goldDim,
    }).setInteractive({ useHandCursor: true });
    copyBtn.on("pointerover", () => copyBtn.setColor(THEME.colors.gold));
    copyBtn.on("pointerout", () => copyBtn.setColor(THEME.colors.goldDim));
    copyBtn.on("pointerdown", () => {
      if (this.friendCode) {
        void navigator.clipboard.writeText(this.friendCode);
        void showAlert(this, "Copied", `"${this.friendCode}" copied to clipboard.`);
      }
    });

    // FIND PLAYERS button on the right
    menuButton(this, CONTENT_X + CONTENT_W - 100, HERO_Y + HERO_H / 2, 170, 36, "🔎 FIND PLAYERS", {
      name: "find-players",
      fontSize: 13,
      display: true,
      onClick: () => this.openFindPlayersModal(),
    });

    // Load friend code
    void fetchMyProfile().then(p => {
      if (!this.scene.isActive()) return;
      this.friendCode = p.friendCode ?? "";
      codeLabel.setText(this.friendCode || "N/A");
    });
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Tab Bar                                                                */
  /* ──────────────────────────────────────────────────────────────────────── */

  private drawTabBar(): void {
    const tabs: Tab[] = ["FRIENDS", "PENDING", "BLOCKED"];
    let tx = CONTENT_X + 8;
    const tabY = TAB_Y;

    for (const t of tabs) {
      const isActive = t === this.activeTab;
      const lbl = this.add.text(tx, tabY, t, {
        fontFamily: THEME.fonts.display, fontSize: "16px",
        color: isActive ? THEME.colors.gold : THEME.colors.parchmentDim,
      }).setInteractive({ useHandCursor: true });

      lbl.on("pointerdown", () => {
        if (this.activeTab === t) return;
        this.activeTab = t;
        this.page = 0;
        this.updateTabHighlights();
        void this.refreshTab();
      });
      lbl.on("pointerover", () => lbl.setColor(THEME.colors.gold));
      lbl.on("pointerout", () => lbl.setColor(t === this.activeTab ? THEME.colors.gold : THEME.colors.parchmentDim));
      this.tabLabels.push({ tab: t, label: lbl });

      // Badge for PENDING
      if (t === "PENDING") {
        const badge = this.add.text(tx + lbl.width + 6, tabY - 2, "", {
          fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.blood,
        });
        this.tabContainers.push(this.add.container(0, 0, [badge]));
      }
      tx += lbl.width + 40;
    }

    // Refresh ↻ icon on the right
    const refreshBtn = this.add.text(CONTENT_X + CONTENT_W - 20, tabY + 2, "↻", {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.parchmentDim,
    }).setOrigin(1, 0).setInteractive({ useHandCursor: true });
    refreshBtn.on("pointerover", () => refreshBtn.setColor(THEME.colors.gold));
    refreshBtn.on("pointerout", () => refreshBtn.setColor(THEME.colors.parchmentDim));
    refreshBtn.on("pointerdown", () => void this.refreshTab());

    // Underline
    this.add.rectangle(CONTENT_X, tabY + 26, CONTENT_W, 2, THEME.hex.gold, 0.25).setOrigin(0);
  }

  private updateTabHighlights(): void {
    for (const { tab, label } of this.tabLabels) {
      label.setColor(tab === this.activeTab ? THEME.colors.gold : THEME.colors.parchmentDim);
    }
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Pre-load counts (for badge)                                            */
  /* ──────────────────────────────────────────────────────────────────────── */

  private async preloadCounts(): Promise<void> {
    const pending = await fetchPendingRequests();
    if (!this.scene.isActive()) return;
    this.pendingInCount = pending.filter(p => p.status === "pending_in").length;
    // Update badge
    if (this.tabContainers[0]) {
      const badge = this.tabContainers[0].list[0] as Phaser.GameObjects.Text;
      badge.setText(this.pendingInCount > 0 ? `${this.pendingInCount}` : "");
    }
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Tab refresh                                                            */
  /* ──────────────────────────────────────────────────────────────────────── */

  private clearList(): void {
    this.listGroup.clear(true, true);
  }

  private async refreshTab(): Promise<void> {
    const version = ++this.requestVersion;
    this.clearList();

    const loadingText = this.add.text(GAME_WIDTH / 2, LIST_Y + 120, "Loading…", {
      fontFamily: THEME.fonts.body, fontSize: "18px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5);
    this.listGroup.add(loadingText);

    let items: FriendSummary[] = [];
    try {
      if (this.activeTab === "FRIENDS")  items = await fetchFriends();
      else if (this.activeTab === "PENDING") items = await fetchPendingRequests();
      else items = await fetchBlocked();
    } catch {
      if (!this.scene.isActive() || version !== this.requestVersion) return;
      this.clearList();
      const errText = this.add.text(GAME_WIDTH / 2, LIST_Y + 120, "Failed to load.", {
        fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.blood,
      }).setOrigin(0.5);
      this.listGroup.add(errText);
      return;
    }

    if (!this.scene.isActive() || version !== this.requestVersion) return;
    this.clearList();

    if (items.length === 0) {
      this.renderEmptyState();
      return;
    }

    if (this.activeTab === "PENDING") {
      this.renderPendingSplit(items);
    } else if (this.activeTab === "FRIENDS") {
      this.renderFriendsList(items);
    } else {
      this.renderBlockedList(items);
    }
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Empty States                                                           */
  /* ──────────────────────────────────────────────────────────────────────── */

  private renderEmptyState(): void {
    const y = LIST_Y + 140;
    if (this.activeTab === "FRIENDS") {
      const title = this.add.text(GAME_WIDTH / 2, y, "No friends yet", {
        fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.parchment,
      }).setOrigin(0.5);
      const detail = this.add.text(GAME_WIDTH / 2, y + 34, "Share your friend code or use Find Players to connect.", {
        fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
        align: "center", wordWrap: { width: 520 },
      }).setOrigin(0.5, 0);
      this.listGroup.addMultiple([title, detail]);
    } else if (this.activeTab === "PENDING") {
      const title = this.add.text(GAME_WIDTH / 2, y, "All caught up", {
        fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.parchment,
      }).setOrigin(0.5);
      const detail = this.add.text(GAME_WIDTH / 2, y + 34, "No pending friend requests right now.", {
        fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
        align: "center", wordWrap: { width: 520 },
      }).setOrigin(0.5, 0);
      this.listGroup.addMultiple([title, detail]);
    } else {
      const title = this.add.text(GAME_WIDTH / 2, y, "No blocked players", {
        fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.parchment,
      }).setOrigin(0.5);
      const detail = this.add.text(GAME_WIDTH / 2, y + 34, "Users you block will appear here.", {
        fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
        align: "center", wordWrap: { width: 520 },
      }).setOrigin(0.5, 0);
      this.listGroup.addMultiple([title, detail]);
    }
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  FRIENDS tab                                                            */
  /* ──────────────────────────────────────────────────────────────────────── */

  private renderFriendsList(items: FriendSummary[]): void {
    const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    this.page = Phaser.Math.Clamp(this.page, 0, pageCount - 1);
    const visible = items.slice(this.page * PAGE_SIZE, (this.page + 1) * PAGE_SIZE);

    let cardY = LIST_Y;
    for (const item of visible) {
      this.renderFriendCard(item, cardY);
      cardY += CARD_H + CARD_GAP;
    }
    this.renderPager(pageCount);
  }

  private renderFriendCard(item: FriendSummary, y: number): void {
    const bg = panel(this, CONTENT_X, y, CONTENT_W, CARD_H, "iron-thin");
    this.listGroup.add(bg);

    // Clickable background for view profile
    const hitZone = this.add.rectangle(CONTENT_X, y, CONTENT_W - 50, CARD_H, 0x000000, 0)
      .setOrigin(0).setInteractive({ useHandCursor: true });
    hitZone.on("pointerover", () => bg.setAlpha(0.85));
    hitZone.on("pointerout", () => bg.setAlpha(1));
    hitZone.on("pointerdown", () => {
      this.registry.set("viewProfileUserId", item.userId);
      goTo(this, "ProfileScene");
    });
    this.listGroup.add(hitZone);

    // Avatar
    const avatar = drawAvatar(this, CONTENT_X + 40, y + CARD_H / 2, 40, item.avatarId, item.userId);
    this.listGroup.add(avatar);

    // Name (bold)
    const nameText = this.add.text(CONTENT_X + 72, y + 10, formatNameWithTitle(item.displayName, item.avatarId), {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.parchment,
    });
    this.listGroup.add(nameText);

    // Friend code + since date
    const since = item.since ? `  •  Friends since ${fmtDate(item.since)}` : "";
    const subText = this.add.text(CONTENT_X + 72, y + 32, `${item.friendCode}${since}`, {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.parchmentDim,
    });
    this.listGroup.add(subText);

    // "View" hint on the right side (before the ⋯ menu)
    const viewHint = this.add.text(CONTENT_X + CONTENT_W - 72, y + CARD_H / 2, "View ›", {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.goldDim,
    }).setOrigin(1, 0.5);
    this.listGroup.add(viewHint);

    // ⋯ overflow menu
    this.addOverflowMenu(item, y);
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  ⋯ Overflow Menu                                                        */
  /* ──────────────────────────────────────────────────────────────────────── */

  private addOverflowMenu(item: FriendSummary, cardY: number): void {
    const dotX = CONTENT_X + CONTENT_W - 24;
    const dotY = cardY + CARD_H / 2;

    const dots = this.add.text(dotX, dotY, "⋯", {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    this.listGroup.add(dots);

    dots.on("pointerover", () => dots.setColor(THEME.colors.gold));
    dots.on("pointerout", () => dots.setColor(THEME.colors.parchmentDim));
    dots.on("pointerdown", () => {
      this.showOverflowPopup(item, dotX, dotY);
    });
  }

  private showOverflowPopup(item: FriendSummary, anchorX: number, anchorY: number): void {
    const popupW = 150;
    const itemH = 32;
    const actions = [
      { label: "Remove", color: THEME.colors.blood, action: () => this.confirmRemove(item) },
      { label: "Block",  color: THEME.colors.blood, action: () => this.confirmBlock(item) },
    ];
    const popupH = actions.length * itemH + 8;
    const px = anchorX - popupW + 10;
    const py = anchorY + 16;

    // Dismiss overlay (invisible full-screen blocker). Above the quit X
    // (overlay+10) so a stray click can't leak through to leave the scene.
    const dismissBg = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.01)
      .setOrigin(0).setInteractive().setDepth(THEME.depths.tooltip);

    const popupBg = panel(this, px, py, popupW, popupH, "iron")
      .setDepth(THEME.depths.tooltip + 1);

    const popupItems: Phaser.GameObjects.GameObject[] = [dismissBg, popupBg];

    actions.forEach((act, idx) => {
      const iy = py + 4 + idx * itemH;
      const label = this.add.text(px + popupW / 2, iy + itemH / 2, act.label, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: act.color,
      }).setOrigin(0.5).setDepth(THEME.depths.tooltip + 2).setInteractive({ useHandCursor: true });
      label.on("pointerover", () => label.setColor(THEME.colors.parchment));
      label.on("pointerout", () => label.setColor(act.color));
      label.on("pointerdown", () => {
        closePopup();
        act.action();
      });
      popupItems.push(label);
    });

    const closePopup = () => {
      popupItems.forEach(o => o.destroy());
    };
    dismissBg.on("pointerdown", closePopup);
  }

  private confirmRemove(item: FriendSummary): void {
    void showConfirm(this, "Remove Friend", `Remove ${item.displayName} from friends?`).then(async yes => {
      if (!yes) return;
      const res = await removeFriend(item.userId);
      if (!this.scene.isActive()) return;
      if (await reportFriends(this, res)) void this.refreshTab();
    });
  }

  private confirmBlock(item: FriendSummary): void {
    void showConfirm(this, "Block Player", `Block ${item.displayName}?`).then(async yes => {
      if (!yes) return;
      const res = await blockUser(item.userId);
      if (!this.scene.isActive()) return;
      if (await reportFriends(this, res)) void this.refreshTab();
    });
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  PENDING tab — split INCOMING / SENT                                    */
  /* ──────────────────────────────────────────────────────────────────────── */

  private renderPendingSplit(items: FriendSummary[]): void {
    const incoming = items.filter(i => i.status === "pending_in");
    const sent = items.filter(i => i.status === "pending_out");

    let y = LIST_Y;

    // ── INCOMING Section ──
    const inLabel = this.add.text(CONTENT_X + 10, y, `INCOMING (${incoming.length})`, {
      fontFamily: THEME.fonts.display, fontSize: "14px", color: THEME.colors.gold,
    });
    this.listGroup.add(inLabel);
    y += 26;

    if (incoming.length === 0) {
      const noIncoming = this.add.text(CONTENT_X + 10, y, "No incoming requests.", {
        fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
      });
      this.listGroup.add(noIncoming);
      y += 28;
    } else {
      for (const item of incoming) {
        if (y + CARD_H > LIST_BOTTOM - 50) break;
        this.renderPendingInCard(item, y);
        y += CARD_H + CARD_GAP;
      }
    }

    y += 10;

    // ── SENT Section ──
    const sentLabel = this.add.text(CONTENT_X + 10, y, `SENT (${sent.length})`, {
      fontFamily: THEME.fonts.display, fontSize: "14px", color: THEME.colors.parchmentDim,
    });
    this.listGroup.add(sentLabel);
    y += 26;

    if (sent.length === 0) {
      const noSent = this.add.text(CONTENT_X + 10, y, "No outgoing requests.", {
        fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
      });
      this.listGroup.add(noSent);
    } else {
      for (const item of sent) {
        if (y + CARD_H > LIST_BOTTOM - 10) break;
        this.renderPendingOutCard(item, y);
        y += CARD_H + CARD_GAP;
      }
    }
  }

  private renderPendingInCard(item: FriendSummary, y: number): void {
    const bg = panel(this, CONTENT_X, y, CONTENT_W, CARD_H, "iron-thin");
    this.listGroup.add(bg);

    const avatar = drawAvatar(this, CONTENT_X + 40, y + CARD_H / 2, 40, item.avatarId, item.userId);
    this.listGroup.add(avatar);

    const nameText = this.add.text(CONTENT_X + 72, y + 12, formatNameWithTitle(item.displayName, item.avatarId), {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.parchment,
    });
    this.listGroup.add(nameText);

    const codeText = this.add.text(CONTENT_X + 72, y + 34, item.friendCode, {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.parchmentDim,
    });
    this.listGroup.add(codeText);

    // ACCEPT button (gold, prominent)
    const acceptBtn = menuButton(this, CONTENT_X + CONTENT_W - 68, y + CARD_H / 2, 110, 30, "ACCEPT", {
      name: `accept-${item.userId.slice(0, 8)}`,
      fontSize: 13,
      display: true,
      onClick: () => {
        void respondToRequest(item.userId, true).then(async res => {
          if (!this.scene.isActive()) return;
          if (await reportFriends(this, res)) void this.refreshTab();
        });
      },
    });
    this.listGroup.add(acceptBtn);

    // DECLINE button (dim)
    const declineBtn = menuButton(this, CONTENT_X + CONTENT_W - 190, y + CARD_H / 2, 110, 30, "DECLINE", {
      name: `decline-${item.userId.slice(0, 8)}`,
      fontSize: 13,
      color: THEME.colors.parchmentDim,
      onClick: () => {
        void respondToRequest(item.userId, false).then(async res => {
          if (!this.scene.isActive()) return;
          if (await reportFriends(this, res)) void this.refreshTab();
        });
      },
    });
    this.listGroup.add(declineBtn);
  }

  private renderPendingOutCard(item: FriendSummary, y: number): void {
    const bg = panel(this, CONTENT_X, y, CONTENT_W, CARD_H, "iron-thin");
    this.listGroup.add(bg);

    const avatar = drawAvatar(this, CONTENT_X + 40, y + CARD_H / 2, 40, item.avatarId, item.userId);
    this.listGroup.add(avatar);

    const nameText = this.add.text(CONTENT_X + 72, y + 12, formatNameWithTitle(item.displayName, item.avatarId), {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.parchment,
    });
    this.listGroup.add(nameText);

    const codeText = this.add.text(CONTENT_X + 72, y + 34, item.friendCode, {
      fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.parchmentDim,
    });
    this.listGroup.add(codeText);

    // CANCEL button
    const cancelBtn = menuButton(this, CONTENT_X + CONTENT_W - 68, y + CARD_H / 2, 110, 30, "CANCEL", {
      name: `cancel-${item.userId.slice(0, 8)}`,
      fontSize: 13,
      color: THEME.colors.parchmentDim,
      onClick: () => {
        void cancelOutgoingRequest(item.userId).then(async res => {
          if (!this.scene.isActive()) return;
          if (await reportFriends(this, res)) void this.refreshTab();
        });
      },
    });
    this.listGroup.add(cancelBtn);
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  BLOCKED tab                                                            */
  /* ──────────────────────────────────────────────────────────────────────── */

  private renderBlockedList(items: FriendSummary[]): void {
    const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    this.page = Phaser.Math.Clamp(this.page, 0, pageCount - 1);
    const visible = items.slice(this.page * PAGE_SIZE, (this.page + 1) * PAGE_SIZE);

    let cardY = LIST_Y;
    for (const item of visible) {
      const bg = panel(this, CONTENT_X, cardY, CONTENT_W, CARD_H, "iron-thin");
      this.listGroup.add(bg);

      const avatar = drawAvatar(this, CONTENT_X + 40, cardY + CARD_H / 2, 40, item.avatarId, item.userId);
      this.listGroup.add(avatar);

      const nameText = this.add.text(CONTENT_X + 72, cardY + 12, formatNameWithTitle(item.displayName, item.avatarId), {
        fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.parchment,
      });
      this.listGroup.add(nameText);

      const codeText = this.add.text(CONTENT_X + 72, cardY + 34, item.friendCode, {
        fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.parchmentDim,
      });
      this.listGroup.add(codeText);

      const unblockBtn = menuButton(this, CONTENT_X + CONTENT_W - 68, cardY + CARD_H / 2, 110, 30, "UNBLOCK", {
        name: `unblock-${item.userId.slice(0, 8)}`,
        fontSize: 13,
        display: true,
        onClick: () => {
          void unblockUser(item.userId).then(async res => {
            if (!this.scene.isActive()) return;
            if (await reportFriends(this, res)) void this.refreshTab();
          });
        },
      });
      this.listGroup.add(unblockBtn);

      cardY += CARD_H + CARD_GAP;
    }
    this.renderPager(pageCount);
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Pager                                                                  */
  /* ──────────────────────────────────────────────────────────────────────── */

  private renderPager(pageCount: number): void {
    if (pageCount <= 1) return;

    const pagerY = GAME_HEIGHT - 44;

    const prev = menuButton(this, GAME_WIDTH / 2 - 120, pagerY, 100, 30, "‹ PREV", {
      name: "page-prev",
      fontSize: 13,
      enabled: this.page > 0,
      onClick: () => { this.page--; void this.refreshTab(); },
    });
    this.listGroup.add(prev);

    const next = menuButton(this, GAME_WIDTH / 2 + 120, pagerY, 100, 30, "NEXT ›", {
      name: "page-next",
      fontSize: 13,
      enabled: this.page < pageCount - 1,
      onClick: () => { this.page++; void this.refreshTab(); },
    });
    this.listGroup.add(next);

    const pageText = this.add.text(GAME_WIDTH / 2, pagerY, `${this.page + 1} / ${pageCount}`, {
      fontFamily: THEME.fonts.display, fontSize: "14px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5);
    this.listGroup.add(pageText);
  }

  /* ──────────────────────────────────────────────────────────────────────── */
  /*  Find Players Modal                                                     */
  /* ──────────────────────────────────────────────────────────────────────── */

  private openFindPlayersModal(): void {
    const MODAL_W = 600;
    const MODAL_H = 480;
    const mx = (GAME_WIDTH - MODAL_W) / 2;
    const my = (GAME_HEIGHT - MODAL_H) / 2;
    const depth = THEME.depths.tooltip;

    let activeModalTab: "search" | "code" = "search";
    let modalResultGroup: Phaser.GameObjects.GameObject[] = [];
    let currentInput: HTMLInputElement | null = null;

    // Blocker
    const blocker = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0).setDepth(depth).setInteractive();

    // Modal panel
    const modalBg = panel(this, mx, my, MODAL_W, MODAL_H, "iron").setDepth(depth + 1);

    // Title
    const title = this.add.text(GAME_WIDTH / 2, my + 22, "FIND PLAYERS", {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.gold,
    }).setOrigin(0.5).setDepth(depth + 2);

    // Tab buttons
    const tabY = my + 56;
    const searchTabLabel = this.add.text(mx + MODAL_W / 2 - 100, tabY, "SEARCH BY NAME", {
      fontFamily: THEME.fonts.display, fontSize: "14px", color: THEME.colors.gold,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(depth + 2);

    const codeTabLabel = this.add.text(mx + MODAL_W / 2 + 100, tabY, "ADD BY CODE", {
      fontFamily: THEME.fonts.display, fontSize: "14px", color: THEME.colors.parchmentDim,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(depth + 2);

    // Tab underline
    const tabUnderline = this.add.rectangle(mx + 20, tabY + 14, MODAL_W - 40, 2, THEME.hex.gold, 0.25)
      .setOrigin(0).setDepth(depth + 2);

    const updateTabColors = () => {
      searchTabLabel.setColor(activeModalTab === "search" ? THEME.colors.gold : THEME.colors.parchmentDim);
      codeTabLabel.setColor(activeModalTab === "code" ? THEME.colors.gold : THEME.colors.parchmentDim);
    };

    // Input element
    const createInput = (placeholder: string) => {
      if (currentInput) currentInput.remove();
      const input = document.createElement("input");
      input.type = "text";
      input.placeholder = placeholder;
      input.maxLength = 64;
      input.spellcheck = false;
      input.autocomplete = "off";
      Object.assign(input.style, {
        position: "absolute",
        width: `${MODAL_W - 80}px`,
        padding: "8px 12px",
        fontFamily: "'IM Fell English', 'Trebuchet MS', serif",
        fontSize: "16px",
        color: "#1a1108",
        background: "#f2e2b8",
        border: "1px solid #8a6a3a",
        borderRadius: "4px",
        outline: "none",
        zIndex: "9999",
        boxSizing: "border-box",
      } as Partial<CSSStyleDeclaration>);
      document.body.appendChild(input);

      const reposition = () => {
        const rect = this.game.canvas.getBoundingClientRect();
        const sx = rect.width / GAME_WIDTH;
        const sy = rect.height / GAME_HEIGHT;
        input.style.left = `${rect.left + (mx + 40) * sx}px`;
        input.style.top = `${rect.top + (tabY + 30) * sy}px`;
        input.style.transform = `scale(${sx}, ${sy})`;
        input.style.transformOrigin = "top left";
      };
      reposition();
      window.addEventListener("resize", reposition);

      currentInput = input;
      setTimeout(() => { input.focus(); }, 0);

      return { input, reposition };
    };

    const clearModalResults = () => {
      modalResultGroup.forEach(o => o.destroy());
      modalResultGroup = [];
    };

    // Render search results inside modal
    const renderModalResults = (results: PlayerSearchResult[], existingFriendIds: Set<string>) => {
      clearModalResults();
      const startY = tabY + 80;
      const cardH = 56;
      const cardGap = 4;

      if (results.length === 0) {
        const noResults = this.add.text(GAME_WIDTH / 2, startY + 60, "No players found.", {
          fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
        }).setOrigin(0.5).setDepth(depth + 2);
        modalResultGroup.push(noResults);
        return;
      }

      const maxVisible = Math.min(results.length, 5);
      for (let i = 0; i < maxVisible; i++) {
        const r = results[i];
        const ry = startY + i * (cardH + cardGap);

        const cardBg = panel(this, mx + 20, ry, MODAL_W - 40, cardH, "iron-thin").setDepth(depth + 2);
        modalResultGroup.push(cardBg);

        const av = drawAvatar(this, mx + 50, ry + cardH / 2, 34, r.avatarId, r.userId);
        av.setDepth(depth + 3);
        modalResultGroup.push(av);

        const nm = this.add.text(mx + 76, ry + 8, formatNameWithTitle(r.displayName, r.avatarId), {
          fontFamily: THEME.fonts.display, fontSize: "14px", color: THEME.colors.parchment,
        }).setDepth(depth + 3);
        modalResultGroup.push(nm);

        const fc = this.add.text(mx + 76, ry + 28, r.friendCode, {
          fontFamily: THEME.fonts.body, fontSize: "11px", color: THEME.colors.parchmentDim,
        }).setDepth(depth + 3);
        modalResultGroup.push(fc);

        const isFriend = existingFriendIds.has(r.userId);
        const isMe = r.userId === getUserId();

        if (isMe) {
          const youLabel = this.add.text(mx + MODAL_W - 60, ry + cardH / 2, "You", {
            fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.parchmentDim,
          }).setOrigin(0.5).setDepth(depth + 3);
          modalResultGroup.push(youLabel);
        } else if (isFriend) {
          const friendChip = this.add.text(mx + MODAL_W - 60, ry + cardH / 2, "✓ Friends", {
            fontFamily: THEME.fonts.body, fontSize: "12px", color: "#6bc46b",
          }).setOrigin(0.5).setDepth(depth + 3);
          modalResultGroup.push(friendChip);
        } else {
          const addBtn = menuButton(this, mx + MODAL_W - 60, ry + cardH / 2, 70, 26, "ADD", {
            name: `modal-add-${r.userId.slice(0, 8)}`,
            fontSize: 12,
            display: true,
            onClick: () => {
              void sendFriendRequest(r.userId).then(async res => {
                if (!this.scene.isActive()) return;
                if (res.ok) {
                  // Replace the button with a "Pending" chip
                  addBtn.destroy();
                  const pending = this.add.text(mx + MODAL_W - 60, ry + cardH / 2, "Pending", {
                    fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.goldDim,
                  }).setOrigin(0.5).setDepth(depth + 3);
                  modalResultGroup.push(pending);
                } else {
                  await showAlert(this, "Error", res.error);
                }
              });
            },
          });
          addBtn.setDepth(depth + 3);
          modalResultGroup.push(addBtn);
        }
      }
    };

    // "Search" tab logic
    const setupSearchTab = () => {
      clearModalResults();
      const { input, reposition } = createInput("Search by player name…");
      let debounceTimer: ReturnType<typeof setTimeout> | null = null;

      input.addEventListener("input", () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          const q = input.value.trim();
          if (q.length < 2) { clearModalResults(); return; }

          // Load existing friends for status chips
          const [results, friends] = await Promise.all([
            searchPlayers(q),
            fetchFriends(),
          ]);
          if (!this.scene.isActive()) return;

          const friendIds = new Set(friends.map(f => f.userId));
          renderModalResults(results, friendIds);
        }, 350);
      });

      input.addEventListener("keydown", ev => {
        if (ev.key === "Escape") { ev.preventDefault(); closeModal(); }
      });

      // Store cleanup for reposition listener
      return () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        window.removeEventListener("resize", reposition);
      };
    };

    // "Code" tab logic
    const setupCodeTab = () => {
      clearModalResults();
      const { input, reposition } = createInput("Enter friend code (e.g. ABCD-EFGH-IJKL-MNOP)");

      input.addEventListener("keydown", async ev => {
        if (ev.key === "Escape") { ev.preventDefault(); closeModal(); return; }
        if (ev.key === "Enter") {
          ev.preventDefault();
          const code = input.value.trim();
          if (!code) return;

          const res = await sendFriendRequestByCode(code);
          if (!this.scene.isActive()) return;
          if (res.ok) {
            clearModalResults();
            const successText = this.add.text(GAME_WIDTH / 2, tabY + 120, "✓ Friend request sent!", {
              fontFamily: THEME.fonts.display, fontSize: "18px", color: "#6bc46b",
            }).setOrigin(0.5).setDepth(depth + 2);
            modalResultGroup.push(successText);
            input.value = "";
          } else {
            await showAlert(this, "Error", res.error);
          }
        }
      });

      const hint = this.add.text(GAME_WIDTH / 2, tabY + 80, "Press ENTER to send a friend request.", {
        fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim,
      }).setOrigin(0.5).setDepth(depth + 2);
      modalResultGroup.push(hint);

      return () => {
        window.removeEventListener("resize", reposition);
      };
    };

    let cleanupTabFn: (() => void) | null = null;

    const switchTab = (tab: "search" | "code") => {
      if (cleanupTabFn) cleanupTabFn();
      activeModalTab = tab;
      updateTabColors();
      if (tab === "search") {
        cleanupTabFn = setupSearchTab();
      } else {
        cleanupTabFn = setupCodeTab();
      }
    };

    searchTabLabel.on("pointerdown", () => switchTab("search"));
    codeTabLabel.on("pointerdown", () => switchTab("code"));

    // Close button
    const closeBtn = menuButton(this, GAME_WIDTH / 2, my + MODAL_H - 32, 130, 34, "CLOSE", {
      name: "modal-close",
      fontSize: 14,
      color: THEME.colors.parchmentDim,
      onClick: () => closeModal(),
    });
    closeBtn.setDepth(depth + 2);

    const closeModal = () => {
      if (cleanupTabFn) cleanupTabFn();
      if (currentInput) { currentInput.remove(); currentInput = null; }
      clearModalResults();
      blocker.destroy();
      modalBg.destroy();
      title.destroy();
      searchTabLabel.destroy();
      codeTabLabel.destroy();
      tabUnderline.destroy();
      closeBtn.destroy();
      // Refresh the underlying list in case friend state changed
      void this.refreshTab();
    };

    // Default to search tab
    switchTab("search");
  }
}
